/** IANA timezone the business treats as "today" for activation/expiration cutoffs. */
export const BUSINESS_TIMEZONE = 'America/Denver'

/**
 * Returns the calendar date (YYYY-MM-DD) of an instant in the business timezone.
 *
 * This reads the CLIENT clock, so it is only a fallback. Prefer the
 * server-provided date (`timeData.currentDate` from /api/time, NTP-backed and
 * already in America/Denver) so a wrong or tampered system clock can't change
 * the result. The timezone is fixed, so the browser's timezone setting never
 * matters, and DST is handled by the IANA zone.
 *
 * The parts are assembled by hand (rather than relying on a locale such as
 * en-CA that happens to format as ISO) so the output format is explicit.
 *
 * @param {Date} [now=new Date()] - Instant to convert (injectable for tests).
 * @returns {string} The business-timezone calendar date as YYYY-MM-DD.
 */
export function getBusinessToday(now: Date = new Date()): string {
   const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: BUSINESS_TIMEZONE,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
   }).formatToParts(now)
   const get = (type: Intl.DateTimeFormatPartTypes): string =>
      parts.find((p) => p.type === type)?.value ?? ''
   return `${get('year')}-${get('month')}-${get('day')}`
}

/**
 * Whether a calendar date is strictly before "today". Plain YYYY-MM-DD strings
 * compare correctly lexicographically, so no Date parsing (and no timezone
 * ambiguity) is involved.
 *
 * @param {string} dateStr - Date as YYYY-MM-DD (any time suffix is ignored).
 * @param {string | null | undefined} [serverToday] - Server-provided today
 *   (YYYY-MM-DD, America/Denver). Falls back to the client clock only when absent.
 * @returns {boolean} True if dateStr is before today.
 */
export function isBeforeBusinessToday(
   dateStr: string,
   serverToday?: string | null,
): boolean {
   const today = serverToday ? serverToday.split('T')[0] : getBusinessToday()
   return dateStr.split('T')[0] < today
}
